import {
  Component,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { CompanyDetails } from '../../models/company-details';
import { DestroyBase } from '../../base-classes/destroy-component-base';
import { SearchService } from '../../../pages/search/services/search.service';
import { RecordScore } from '../../interfaces/record-score';
import { takeUntil } from 'rxjs';

@Component({
  selector: 'app-record-scores',
  standalone: true,
  imports: [DecimalPipe],
  templateUrl: './record-scores.component.html',
  styleUrl: './record-scores.component.scss',
})
export class RecordScoresComponent extends DestroyBase implements OnChanges {
  @Input() recordDetails: CompanyDetails | undefined;
  @Input() showLoanScore = false;
  @Input() useCustomData: boolean | null = null;
  @Input() source: string | undefined;

  searchService = inject(SearchService);

  isBusinessOverviewExpanded = false;
  pqmScores: RecordScore | undefined;
  isLoadingScore = false;
  scoreError: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['showLoanScore'] &&
      changes['showLoanScore'].currentValue &&
      this.recordDetails
    ) {
      this.isLoadingScore = true;
      this.scoreError = null;

      this.searchService
        .getRecordLoanScore(
          this.recordDetails.unique_id,
          this.useCustomData ?? undefined,
          this.source,
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.pqmScores = res;
            if (this.pqmScores.score.sc_pqm > 10) {
              this.pqmScores.score.sc_pqm = 10;
            }
            if (this.pqmScores?.score?.failed) {
              this.scoreError =
                'Score calculation failed due to missing required input(s).';
            }
            this.isLoadingScore = false;
          },
          error: (error) => {
            if (error.error[0]?.includes('score calculation failed')) {
              this.scoreError =
                'Score calculation failed due to missing required input(s).';
            } else {
              this.scoreError =
                'Failed to load loan score data. Please try again.';
            }
            this.isLoadingScore = false;
          },
        });
    }
  }

  getPropensityScore(propertyName: string): number | string {
    if (this.pqmScores) {
      if (propertyName === 'Total Score') {
        if (this.pqmScores.score.sc_pqm) {
          return Number(this.pqmScores.score.sc_pqm.toFixed(2));
        } else {
          return 'NA';
        }
      }
      if (propertyName === 'Business Background Score') {
        if (this.pqmScores.score.sc_bb) {
          return Number(this.pqmScores.score.sc_bb.toFixed(2));
        } else {
          return 'NA';
        }
      }
      if (propertyName === 'Debt Service') {
        if (this.pqmScores.score.sc_ds) {
          return Number(this.pqmScores.score.sc_ds.toFixed(2));
        } else {
          return 'NA';
        }
      }
      if (propertyName === 'Requested Credit Facility') {
        if (this.pqmScores.score.sc_fc) {
          return Number(this.pqmScores.score.sc_fc.toFixed(2));
        } else {
          return 'NA';
        }
      }
    } else {
      const property = this.recordDetails?.detail?.properties.find(
        (p) => p.property_name === propertyName,
      );
      if (!property?.value) return '';

      if (property.value === 'KO') return 'LOW';

      const numValue = Number(property.value);
      if (!isNaN(numValue)) {
        return Number(numValue.toFixed(2));
      }
    }
    return '';
  }

  getScoreClass(score: number | string): string {
    if (
      score === 'LOW' ||
      score === '' ||
      typeof score !== 'number' ||
      score === 0
    )
      return 'low';
    if (score > 5) return 'high';
    if (score > 2) return 'medium';
    return 'low';
  }

  getPropensityClassFromScore(pqmScore: number): string {
    if (pqmScore > 5) return 'HIGH';
    if (pqmScore > 2) return 'MEDIUM';
    return 'LOW';
  }

  getScoreCategory(score: number | string): string {
    if (
      score === 'LOW' ||
      score === '' ||
      typeof score !== 'number' ||
      score === 0
    )
      return 'Low Propensity';
    if (score > 5) return 'High Propensity';
    if (score > 2) return 'Medium Propensity';
    return 'Low Propensity';
  }

  getScoreRangeLabel(score: number | string): string {
    if (
      score === 'LOW' ||
      score === '' ||
      typeof score !== 'number' ||
      score === 0
    )
      return 'Low (0-2 | LOW)';
    if (score > 5) return 'High (5-10)';
    if (score > 2) return 'Medium (2-5)';
    return 'Low (0-2 | LOW)';
  }

  getScorePercentage(score: number | string): number {
    if (
      score === 'LOW' ||
      score === '' ||
      typeof score !== 'number' ||
      score === 0
    )
      return 0;
    const percentage = (score / 10) * 100;
    return Math.min(Math.max(percentage, 0), 100);
  }

  private formatItem(
    key: string,
    options?: Record<string, string | number | boolean> | null,
  ): string {
    if (!key) return '';

    // If already formatted (has spaces and uppercase letters)
    if (/[A-Z]/.test(key) && /\s/.test(key)) {
      return key;
    }

    if (options) {
      const match = Object.keys(options).find(
        (label) =>
          options[label] === key ||
          label.toLowerCase() === key.toLowerCase() ||
          label.toLowerCase().replace(/[\s_-]+/g, '') ===
            key.toLowerCase().replace(/[\s_-]+/g, ''),
      );
      if (match) return match;
    }

    const special: Record<string, string> = {
      real_estate_building: 'Real Estate - Building',
      third_party_guarantees: '3rd Party Guarantees',
      no_collateral: 'No Collateral',
    };
    if (special[key]) return special[key];

    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  getPropertyValue(propertyKey: string): string {
    let property = this.recordDetails?.detail?.properties.find((p) =>
      p.property?.endsWith(`::${propertyKey}`),
    );

    if (!property) {
      property = this.recordDetails?.detail?.properties.find(
        (p) => p.property === propertyKey,
      );
    }

    if (
      !property ||
      property.value === null ||
      property.value === undefined ||
      property.value === ''
    ) {
      return '-';
    }

    // Handle array (multiselect or list)
    if (Array.isArray(property.value)) {
      if (!property.value.length) return '-';
      return property.value
        .map((item) =>
          typeof item === 'string'
            ? this.formatItem(item, property.options)
            : String(item),
        )
        .join(', ');
    }

    // Handle object (e.g. { land: true, no_collateral: true } from form_data_loan)
    if (typeof property.value === 'object') {
      const selected = Object.entries(property.value)
        .filter(([, v]) => v === true || v === 'true')
        .map(([k]) => this.formatItem(k, property.options));
      return selected.length ? selected.join(', ') : '-';
    }

    // Handle contributing_personal_capital boolean
    if (propertyKey === 'contributing_personal_capital') {
      if (typeof property.value === 'boolean') {
        return property.value ? 'Yes' : 'No';
      }
    }

    if (propertyKey === 'business_established' && property.value) {
      const value = property.value;

      // Check if it's just a year (4 digits)
      if (/^\d{4}$/.test(value)) {
        const establishedYear = parseInt(value);
        const currentYear = new Date().getFullYear();
        const years = currentYear - establishedYear;
        return years > 0 ? `${years} Years` : 'Less than a Year';
      }

      // Handle full date
      const establishedDate = new Date(value);
      if (!isNaN(establishedDate.getTime())) {
        const currentDate = new Date();
        const timeDiff = currentDate.getTime() - establishedDate.getTime();
        const yearsDiff = timeDiff / (1000 * 3600 * 24 * 365.25);

        if (yearsDiff >= 1) {
          return `${yearsDiff.toFixed(1)} Years`;
        } else {
          return 'Less than a Year';
        }
      }
    }

    return property.value || '-';
  }

  getFormattedValue(propertyKey: string): string {
    let property = this.recordDetails?.detail?.properties.find((p) =>
      p.property?.endsWith(`::${propertyKey}`),
    );

    if (!property) {
      property = this.recordDetails?.detail?.properties.find(
        (p) => p.property === propertyKey,
      );
    }

    if (!property?.value || property.value === '-') {
      return '-';
    }

    // Check if the value already contains a $ symbol
    let valueToFormat = property.value;
    let hasPrefix = false;

    if (typeof valueToFormat === 'string' && valueToFormat.includes('$')) {
      // Remove $ and any commas to get the raw number
      valueToFormat = valueToFormat.replace(/[$,]/g, '');
      hasPrefix = true;
    }

    // Convert to number and check if it's a valid number
    const numValue = Number(valueToFormat);
    if (!isNaN(numValue) && numValue !== 0) {
      // Use DecimalPipe to format with commas
      const decimalPipe = new DecimalPipe('en-US');
      const formatted = decimalPipe.transform(numValue, '1.0-2');
      return formatted ? (hasPrefix ? `$${formatted}` : formatted) : '-';
    }

    if (typeof property.value === 'object') {
      return '-';
    }

    return property.value;
  }

  toggleBusinessOverview(): void {
    this.isBusinessOverviewExpanded = !this.isBusinessOverviewExpanded;
  }
}
